import { useState } from "react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { setAgentAppearance } from "@/lib/api";
import { useToast } from "@/components/ui/toast";

// AGENT_COLORS is the curated agent palette — MUST stay in sync with the backend
// `agentColors` allowlist (internal/publicapi/appearance.go). A fixed set (not a
// freeform color wheel) keeps avatars legible in light + dark and maps cleanly to
// the 3D-character material. Class strings are written out in full so Tailwind's
// content scanner keeps them (never interpolate color into a class name).
export type AgentColor =
  | "indigo" | "violet" | "blue" | "teal" | "emerald" | "amber" | "rose" | "slate" | "fuchsia";

export const AGENT_COLORS: Record<
  AgentColor,
  { label: string; bg: string; fg: string; solid: string; hex: string }
> = {
  indigo:  { label: "Indigo",  bg: "bg-indigo-500/15",  fg: "text-indigo-600 dark:text-indigo-300",   solid: "bg-indigo-500",  hex: "#6366f1" },
  violet:  { label: "Violet",  bg: "bg-violet-500/15",  fg: "text-violet-600 dark:text-violet-300",   solid: "bg-violet-500",  hex: "#8b5cf6" },
  blue:    { label: "Blue",    bg: "bg-blue-500/15",    fg: "text-blue-600 dark:text-blue-300",       solid: "bg-blue-500",    hex: "#3b82f6" },
  teal:    { label: "Teal",    bg: "bg-teal-500/15",    fg: "text-teal-600 dark:text-teal-300",       solid: "bg-teal-500",    hex: "#14b8a6" },
  emerald: { label: "Emerald", bg: "bg-emerald-500/15", fg: "text-emerald-600 dark:text-emerald-300", solid: "bg-emerald-500", hex: "#10b981" },
  amber:   { label: "Amber",   bg: "bg-amber-500/15",   fg: "text-amber-600 dark:text-amber-300",     solid: "bg-amber-500",   hex: "#f59e0b" },
  rose:    { label: "Rose",    bg: "bg-rose-500/15",    fg: "text-rose-600 dark:text-rose-300",       solid: "bg-rose-500",    hex: "#f43f5e" },
  slate:   { label: "Slate",   bg: "bg-slate-500/15",   fg: "text-slate-600 dark:text-slate-300",     solid: "bg-slate-500",   hex: "#64748b" },
  fuchsia: { label: "Fuchsia", bg: "bg-fuchsia-500/15", fg: "text-fuchsia-600 dark:text-fuchsia-300", solid: "bg-fuchsia-500", hex: "#d946ef" },
};

export const AGENT_COLOR_TOKENS = Object.keys(AGENT_COLORS) as AgentColor[];

// hashStr is a small stable djb2 — same name always picks the same default color.
function hashStr(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i++) h = ((h << 5) + h + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

export function defaultAgentColor(name: string): AgentColor {
  return AGENT_COLOR_TOKENS[hashStr(name || "agent") % AGENT_COLOR_TOKENS.length];
}

// monogram = first letter/digit of the name, uppercased (the unset default glyph).
function monogram(name: string): string {
  const ch = (name || "").trim().match(/[\p{L}\p{N}]/u);
  return ch ? ch[0].toUpperCase() : "•";
}

const SIZES = {
  sm: "h-7 w-7 text-sm",
  md: "h-9 w-9 text-[15px]",
  lg: "h-12 w-12 text-xl",
} as const;

export function AgentAvatar({
  name,
  emoji,
  color,
  size = "md",
  className,
}: {
  name: string;
  emoji?: string | null;
  color?: string | null;
  size?: keyof typeof SIZES;
  className?: string;
}) {
  const c = (color && AGENT_COLORS[color as AgentColor]) || AGENT_COLORS[defaultAgentColor(name)];
  const glyph = emoji?.trim() || monogram(name);
  return (
    <div
      className={cn(
        "flex shrink-0 select-none items-center justify-center rounded-full font-semibold leading-none",
        c.bg,
        c.fg,
        SIZES[size],
        className,
      )}
      aria-hidden
    >
      {glyph}
    </div>
  );
}

const SUGGESTED_EMOJI = [
  "🤖", "✨", "🧠", "📊", "🛠️", "📨", "🗂️", "🔔",
  "🚀", "🛰️", "📈", "🧭", "🔬", "💼", "🌱", "⚙️",
];

// AppearanceDialog lets the owner pick an emoji + color for an agent. Empty emoji
// / color = clear → the deterministic default (hashed color + monogram) returns.
export function AppearanceDialog({
  open,
  onOpenChange,
  projectId,
  name,
  emoji,
  color,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  projectId: string;
  name: string;
  emoji?: string | null;
  color?: string | null;
  onSaved: (emoji: string, color: string) => void;
}) {
  const [em, setEm] = useState(emoji ?? "");
  const [col, setCol] = useState<string>(color ?? "");
  const [busy, setBusy] = useState(false);
  const toast = useToast();

  async function save() {
    setBusy(true);
    try {
      const res = await setAgentAppearance(projectId, { emoji: em.trim(), color: col });
      onSaved(res.emoji, res.color);
      onOpenChange(false);
      toast.success("Agent appearance updated");
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Couldn't save appearance");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[420px]">
        <DialogHeader>
          <DialogTitle>Customize agent</DialogTitle>
          <DialogDescription>Give {name || "this agent"} an emoji and color.</DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-3">
          <AgentAvatar name={name} emoji={em} color={col} size="lg" />
          <div className="text-sm text-muted-foreground">
            Preview — this is how it shows in your agent list and its 3D character.
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="agent-emoji">Emoji</Label>
          <div className="flex items-center gap-2">
            <Input
              id="agent-emoji"
              value={em}
              maxLength={8}
              placeholder="Pick or type one"
              onChange={(e) => setEm(e.target.value)}
              className="w-40 text-center text-lg"
            />
            {em && (
              <Button type="button" variant="ghost" size="sm" onClick={() => setEm("")}>
                Clear
              </Button>
            )}
          </div>
          <div className="flex flex-wrap gap-1.5 pt-1">
            {SUGGESTED_EMOJI.map((e) => (
              <button
                key={e}
                type="button"
                onClick={() => setEm(e)}
                className={cn(
                  "flex h-8 w-8 items-center justify-center rounded-md border text-lg transition-colors hover:bg-accent",
                  em === e && "border-primary bg-accent",
                )}
                aria-label={`Use ${e}`}
              >
                {e}
              </button>
            ))}
          </div>
        </div>

        <div className="space-y-2">
          <Label>Color</Label>
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => setCol("")}
              className={cn(
                "flex h-8 w-8 items-center justify-center rounded-full border text-[10px] font-medium text-muted-foreground transition-transform hover:scale-110",
                col === "" && "ring-2 ring-foreground ring-offset-2 ring-offset-background",
              )}
              title="Auto (from name)"
            >
              Auto
            </button>
            {AGENT_COLOR_TOKENS.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setCol(t)}
                className={cn(
                  "h-8 w-8 rounded-full transition-transform hover:scale-110",
                  AGENT_COLORS[t].solid,
                  col === t && "ring-2 ring-foreground ring-offset-2 ring-offset-background",
                )}
                title={AGENT_COLORS[t].label}
                aria-label={AGENT_COLORS[t].label}
              />
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
            Cancel
          </Button>
          <Button onClick={save} disabled={busy}>
            {busy ? "Saving…" : "Save"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
